import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUserManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalUser, setApprovalUser] = useState(null);
  const [approvalAthleteId, setApprovalAthleteId] = useState('');
  // Phase 8 — Work Areas editor (Admin sets Staff visibility into Work Center).
  const [workAreasCatalog, setWorkAreasCatalog] = useState([]);
  const [editingWorkAreas, setEditingWorkAreas] = useState([]);
  const [savingWorkAreas, setSavingWorkAreas] = useState(false);
  
  // New user form state
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    full_name: '',
    role: '',
    athlete_id: '',
    assigned_athletes: []
  });

  useEffect(() => {
    if (user?.role !== 'Admin') {
      navigate('/dashboard');
      return;
    }
    fetchData();
    // Load Work Areas catalog once. Failure here is non-fatal — the editor
    // simply hides if the catalog cannot be loaded.
    axios.get(`${API}/work-center/work-areas`)
      .then((r) => setWorkAreasCatalog(r.data?.work_areas || []))
      .catch(() => setWorkAreasCatalog([]));
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, pendingRes, athletesRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/users/pending`),
        axios.get(`${API}/athletes`)
      ]);
      setUsers(usersRes.data);
      setPendingUsers(pendingRes.data);
      setAthletes(athletesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.full_name || !newUser.role) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (newUser.role === 'Player' && !newUser.athlete_id) {
      toast.error('Please select an athlete for the Player account');
      return;
    }

    if (newUser.role === 'Parent' && !newUser.athlete_id) {
      toast.error('Please select an athlete for the Parent account');
      return;
    }

    try {
      await axios.post(`${API}/admin/users`, newUser);
      toast.success('User created successfully');
      setCreateDialogOpen(false);
      setNewUser({
        email: '',
        password: '',
        full_name: '',
        role: '',
        athlete_id: '',
        assigned_athletes: []
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleApproveUser = async (approve) => {
    if (!approvalUser) return;
    
    if (approve && (approvalUser.role === 'Player' || approvalUser.role === 'Parent') && !approvalAthleteId) {
      toast.error('Please select an athlete to link this account to');
      return;
    }

    try {
      await axios.patch(`${API}/admin/users/${approvalUser.id}/approve`, {
        is_approved: approve,
        athlete_id: approve ? approvalAthleteId : null
      });
      toast.success(approve ? 'User approved successfully' : 'User rejected');
      setApprovalDialogOpen(false);
      setApprovalUser(null);
      setApprovalAthleteId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    }
  };

  const openApprovalDialog = (pendingUser) => {
    setApprovalUser(pendingUser);
    setApprovalAthleteId('');
    setApprovalDialogOpen(true);
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/status`, {
        is_active: !currentStatus
      });
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleToggleLogAccess = async (userId, currentValue) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/log-access`, {
        can_view_own_logs: !currentValue,
      });
      toast.success(`Log access ${currentValue ? 'revoked' : 'granted'}`);
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update log access');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}`);
      setSelectedUser(response.data);
      setEditingWorkAreas(Array.isArray(response.data?.work_areas) ? response.data.work_areas : []);
      setDetailsDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch user details');
    }
  };

  const toggleWorkArea = (area) => {
    setEditingWorkAreas((prev) =>
      prev.includes(area) ? prev.filter((a) => a !== area) : [...prev, area]
    );
  };

  const saveWorkAreas = async () => {
    if (!selectedUser) return;
    setSavingWorkAreas(true);
    try {
      const updated = await axios.put(
        `${API}/work-center/users/${selectedUser.id}/work-areas`,
        { work_areas: editingWorkAreas },
      );
      toast.success('Work Areas updated');
      setSelectedUser((prev) => prev ? { ...prev, work_areas: updated.data?.work_areas || editingWorkAreas } : prev);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update Work Areas');
    } finally {
      setSavingWorkAreas(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'Admin': 'bg-purple-100 text-purple-800 border-purple-200',
      'Staff': 'bg-blue-100 text-blue-800 border-blue-200',
      'Player': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'Parent': 'bg-amber-100 text-amber-800 border-amber-200'
    };
    return colors[role] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const getStatusBadge = (isActive) => {
    return isActive 
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-red-100 text-red-800 border-red-200';
  };

  const filterUsersByRole = (role) => {
    return users.filter(u => u.role === role);
  };

  const getUnlinkedAthletes = (forRole) => {
    if (forRole === 'Player') {
      return athletes.filter(a => !a.linked_player);
    }
    return athletes; // Parents can be linked to any athlete
  };

  if (user?.role !== 'Admin') {
    return null;
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="mb-6 flex justify-between items-start">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              User Management
            </h2>
            <p className="text-gray-600 dark:text-gray-500 mt-1">Manage user accounts and link them to athletes</p>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-pfap-green hover:bg-pfap-green/90" data-testid="create-user-btn">
                + Create User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
                <DialogDescription>
                  Create a new user account and optionally link to an athlete
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="role">Role *</Label>
                  <Select
                    value={newUser.role}
                    onValueChange={(value) => setNewUser({ ...newUser, role: value, athlete_id: '', assigned_athletes: [] })}
                  >
                    <SelectTrigger data-testid="role-select">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Admin">Admin</SelectItem>
                      <SelectItem value="Staff">Staff</SelectItem>
                      <SelectItem value="Player">Player</SelectItem>
                      <SelectItem value="Parent">Parent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                    placeholder="Enter full name"
                    data-testid="fullname-input"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder="Enter email address"
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="Enter password"
                    data-testid="password-input"
                  />
                </div>

                {(newUser.role === 'Player' || newUser.role === 'Parent') && (
                  <div>
                    <Label htmlFor="athlete">Link to Athlete *</Label>
                    <Select
                      value={newUser.athlete_id}
                      onValueChange={(value) => setNewUser({ ...newUser, athlete_id: value })}
                    >
                      <SelectTrigger data-testid="athlete-select">
                        <SelectValue placeholder="Select athlete" />
                      </SelectTrigger>
                      <SelectContent>
                        {getUnlinkedAthletes(newUser.role).map((athlete) => (
                          <SelectItem key={athlete.id} value={athlete.id}>
                            {athlete.full_name} ({athlete.squad || 'No squad'})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {newUser.role === 'Player' && (
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Only athletes without a player account are shown
                      </p>
                    )}
                  </div>
                )}

                {newUser.role === 'Staff' && (
                  <div>
                    <Label>Assign Athletes (Optional)</Label>
                    <div className="max-h-40 overflow-y-auto border rounded-md p-2 mt-1">
                      {athletes.map((athlete) => (
                        <label key={athlete.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50 dark:bg-gray-900">
                          <input
                            type="checkbox"
                            checked={newUser.assigned_athletes.includes(athlete.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewUser({
                                  ...newUser,
                                  assigned_athletes: [...newUser.assigned_athletes, athlete.id]
                                });
                              } else {
                                setNewUser({
                                  ...newUser,
                                  assigned_athletes: newUser.assigned_athletes.filter(id => id !== athlete.id)
                                });
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{athlete.full_name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateUser} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="submit-create-user">
                  Create User
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          {pendingUsers.length > 0 && (
            <Card className="bg-orange-50 border-orange-300 border-2">
              <CardContent className="pt-6">
                <p className="text-sm text-orange-700 font-medium">Pending Approval</p>
                <p className="text-2xl font-bold text-orange-900">{pendingUsers.length}</p>
              </CardContent>
            </Card>
          )}
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-6">
              <p className="text-sm text-purple-700 font-medium">Admins</p>
              <p className="text-2xl font-bold text-purple-900">{filterUsersByRole('Admin').length}</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <p className="text-sm text-blue-700 font-medium">Staff</p>
              <p className="text-2xl font-bold text-blue-900">{filterUsersByRole('Staff').length}</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="pt-6">
              <p className="text-sm text-emerald-700 font-medium">Players</p>
              <p className="text-2xl font-bold text-emerald-900">{filterUsersByRole('Player').length}</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-6">
              <p className="text-sm text-amber-700 font-medium">Parents</p>
              <p className="text-2xl font-bold text-amber-900">{filterUsersByRole('Parent').length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Pending Users Section */}
        {pendingUsers.length > 0 && (
          <Card className="mb-6 border-orange-200">
            <CardHeader className="bg-orange-50">
              <CardTitle className="flex items-center gap-2 text-orange-800">
                <span className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></span>
                Pending Approvals ({pendingUsers.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-3">
                {pendingUsers.map((pendingUser) => (
                  <div key={pendingUser.id} className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-orange-200 rounded-full flex items-center justify-center text-orange-700 font-bold">
                        {pendingUser.full_name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{pendingUser.full_name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-500">{pendingUser.email}</p>
                      </div>
                      <Badge className={getRoleBadgeColor(pendingUser.role)}>{pendingUser.role}</Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        className="bg-pfap-green hover:bg-pfap-green/90"
                        onClick={() => openApprovalDialog(pendingUser)}
                        data-testid={`approve-btn-${pendingUser.id}`}
                      >
                        Review & Approve
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-red-600 border-red-300"
                        onClick={() => handleDeleteUser(pendingUser.id)}
                        data-testid={`reject-btn-${pendingUser.id}`}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Approval Dialog */}
        <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Approve User Registration</DialogTitle>
              <DialogDescription>
                Review the registration and link to an athlete profile
              </DialogDescription>
            </DialogHeader>
            
            {approvalUser && (
              <div className="space-y-4 py-4">
                <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-gray-500 dark:text-gray-500">Name</p>
                      <p className="font-medium">{approvalUser.full_name}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-500">Email</p>
                      <p className="font-medium">{approvalUser.email}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-500">Role</p>
                      <Badge className={getRoleBadgeColor(approvalUser.role)}>{approvalUser.role}</Badge>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-500">Registered</p>
                      <p className="font-medium text-xs">{new Date(approvalUser.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {(approvalUser.role === 'Player' || approvalUser.role === 'Parent') && (
                  <div>
                    <Label htmlFor="approval-athlete">Link to Athlete *</Label>
                    <Select
                      value={approvalAthleteId}
                      onValueChange={setApprovalAthleteId}
                    >
                      <SelectTrigger data-testid="approval-athlete-select">
                        <SelectValue placeholder="Select athlete to link" />
                      </SelectTrigger>
                      <SelectContent>
                        {getUnlinkedAthletes(approvalUser.role).map((athlete) => (
                          <SelectItem key={athlete.id} value={athlete.id}>
                            {athlete.full_name} ({athlete.squad || 'No squad'})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      {approvalUser.role === 'Player' 
                        ? 'Only athletes without a player account are shown'
                        : 'Select the athlete this parent is responsible for'}
                    </p>
                  </div>
                )}

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800">
                    <strong>Important:</strong> Once approved, this user will be able to log in and access data 
                    for the linked athlete.
                  </p>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setApprovalDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => handleApproveUser(true)} 
                className="bg-pfap-green hover:bg-pfap-green/90"
                data-testid="confirm-approve-btn"
              >
                Approve User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* User Tabs */}
        <Card>
          <Tabs defaultValue="all">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Users</CardTitle>
                <TabsList>
                  <TabsTrigger value="all">All ({users.length})</TabsTrigger>
                  <TabsTrigger value="Admin">Admins</TabsTrigger>
                  <TabsTrigger value="Staff">Staff</TabsTrigger>
                  <TabsTrigger value="Player">Players</TabsTrigger>
                  <TabsTrigger value="Parent">Parents</TabsTrigger>
                </TabsList>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
                </div>
              ) : (
                <>
                  <TabsContent value="all">
                    <UserTable 
                      users={users} 
                      onViewDetails={viewUserDetails}
                      onToggleStatus={handleToggleUserStatus}
                      onToggleLogAccess={handleToggleLogAccess}
                      onDelete={handleDeleteUser}
                      getRoleBadgeColor={getRoleBadgeColor}
                      getStatusBadge={getStatusBadge}
                    />
                  </TabsContent>
                  {['Admin', 'Staff', 'Player', 'Parent'].map(role => (
                    <TabsContent key={role} value={role}>
                      <UserTable 
                        users={filterUsersByRole(role)} 
                        onViewDetails={viewUserDetails}
                        onToggleStatus={handleToggleUserStatus}
                        onToggleLogAccess={handleToggleLogAccess}
                        onDelete={handleDeleteUser}
                        getRoleBadgeColor={getRoleBadgeColor}
                        getStatusBadge={getStatusBadge}
                      />
                    </TabsContent>
                  ))}
                </>
              )}
            </CardContent>
          </Tabs>
        </Card>

        {/* User Details Dialog */}
        <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>User Details</DialogTitle>
              <DialogDescription>
                Review profile information, assigned athletes, and (for Admin / Staff) the Work Areas that scope the Staff Work Center tasks visible to this user.
              </DialogDescription>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center text-2xl font-bold text-emerald-700">
                    {selectedUser.full_name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">{selectedUser.full_name}</h3>
                    <p className="text-gray-500 dark:text-gray-500">{selectedUser.email}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-500">Role</p>
                    <Badge className={getRoleBadgeColor(selectedUser.role)}>{selectedUser.role}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-500">Status</p>
                    <Badge className={getStatusBadge(selectedUser.is_active)}>
                      {selectedUser.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>

                {selectedUser.linked_athlete && (
                  <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                    <p className="text-sm text-gray-500 dark:text-gray-500 mb-2">Linked Athlete</p>
                    <p className="font-medium">{selectedUser.linked_athlete.full_name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-500">
                      {selectedUser.linked_athlete.position} | {selectedUser.linked_athlete.squad || 'No squad'}
                    </p>
                  </div>
                )}

                {selectedUser.assigned_athletes_details && selectedUser.assigned_athletes_details.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                    <p className="text-sm text-gray-500 dark:text-gray-500 mb-2">Assigned Athletes ({selectedUser.assigned_athletes_details.length})</p>
                    <div className="space-y-1">
                      {selectedUser.assigned_athletes_details.map(athlete => (
                        <p key={athlete.id} className="text-sm">
                          • {athlete.full_name} ({athlete.squad || 'No squad'})
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-500">Created</p>
                  <p className="text-sm">{new Date(selectedUser.created_at).toLocaleString()}</p>
                </div>

                {/* Phase 8 — Work Areas editor (Admin assigns Staff scope) */}
                {(selectedUser.role === 'Staff' || selectedUser.role === 'Admin') && workAreasCatalog.length > 0 && (
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg border border-emerald-200 dark:border-emerald-800" data-testid="work-areas-editor">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Work Areas
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                      Controls which Work Center tasks this user sees. Admin sees all tasks regardless of this list.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {workAreasCatalog.map((area) => {
                        const active = editingWorkAreas.includes(area);
                        return (
                          <button
                            type="button"
                            key={area}
                            onClick={() => toggleWorkArea(area)}
                            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                              active
                                ? 'bg-emerald-600 text-white border-emerald-600'
                                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-emerald-400'
                            }`}
                            data-testid={`work-area-chip-${area}`}
                          >
                            {area}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {editingWorkAreas.length === 0
                          ? 'No areas selected — this user will see NO Work Center tasks.'
                          : `${editingWorkAreas.length} area${editingWorkAreas.length === 1 ? '' : 's'} selected.`}
                      </p>
                      <Button
                        size="sm"
                        onClick={saveWorkAreas}
                        disabled={savingWorkAreas}
                        data-testid="save-work-areas-button"
                      >
                        {savingWorkAreas ? 'Saving…' : 'Save Work Areas'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

// Separate UserTable component
const UserTable = ({ users, onViewDetails, onToggleStatus, onToggleLogAccess, onDelete, getRoleBadgeColor, getStatusBadge }) => {
  if (users.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-500">
        <p>No users found</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full" data-testid="users-table">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">User</th>
            <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Email</th>
            <th className="text-center py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Role</th>
            <th className="text-center py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Status</th>
            <th className="text-center py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Linked To</th>
            <th className="text-center py-3 px-4 font-medium text-gray-700 dark:text-gray-300" title="Allows this Staff user to view only their own system-log entries">Log Access</th>
            <th className="text-center py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:bg-gray-900">
              <td className="py-3 px-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-sm font-medium">
                    {user.full_name?.charAt(0) || '?'}
                  </div>
                  <span className="font-medium">{user.full_name}</span>
                </div>
              </td>
              <td className="py-3 px-4 text-gray-600 dark:text-gray-500">{user.email}</td>
              <td className="py-3 px-4 text-center">
                <Badge className={getRoleBadgeColor(user.role)}>{user.role}</Badge>
              </td>
              <td className="py-3 px-4 text-center">
                <Badge className={getStatusBadge(user.is_active)}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </td>
              <td className="py-3 px-4 text-center text-sm text-gray-500 dark:text-gray-500">
                {user.linked_athlete_name || (user.assigned_count > 0 ? `${user.assigned_count} athletes` : '-')}
              </td>
              <td className="py-3 px-4 text-center">
                {user.role === 'Staff' ? (
                  <div className="flex items-center justify-center gap-2">
                    <Switch
                      checked={!!user.can_view_own_logs}
                      onCheckedChange={() => onToggleLogAccess(user.id, user.can_view_own_logs)}
                      data-testid={`log-access-switch-${user.id}`}
                      aria-label="Toggle own-log access"
                    />
                    <span className="text-xs text-gray-500">{user.can_view_own_logs ? 'On' : 'Off'}</span>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">—</span>
                )}
              </td>
              <td className="py-3 px-4 text-center">
                <div className="flex justify-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => onViewDetails(user.id)}>
                    View
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => onToggleStatus(user.id, user.is_active)}
                    className={user.is_active ? 'text-amber-600' : 'text-emerald-600'}
                  >
                    {user.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="text-red-600"
                    onClick={() => onDelete(user.id)}
                  >
                    Delete
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdminUserManagement;
